import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Modal, Backdrop, Fade } from '@material-ui/core';
import Text from '../Typography/Text';
import Button from '../Buttons/Button';
import CloseButton from '../Buttons/CloseButton';
import fontFamily from '../../assets/css/fonts';
import color from '../../assets/css/colors';
import { form1099bDetails } from '../../services/Form1099BService';
import {
  CustomBodyRenderDate,
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderBoolean,
} from '../../components/Table/CustomBodyRender';
import Table, { columnType } from '../../components/ClearingTable/Table';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1500,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 30px',
    maxHeight: 600,
    overflowY: 'auto',
    fontFamily: fontFamily.headers,
    color: color.default.main,
    '& p': {
      fontSize: 16,
      fontFamily: fontFamily.headers,
      fontWeight: 400,
      lineHeight: '26px',
      color: color.default.main,
      marginTop: 0,
      textAlign: 'center',
    },
    '& hr': {
      margin: '30px 0px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: 25,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
  paper: {
    position: 'absolute',
  },
}));

// Table Settings
const form1099BColumns = [
  {
    name: 'dateAcq',
    label: 'Acquired Date',
    options: {
      customBodyRender: CustomBodyRenderDate,
    },
    type: columnType.date,
  },
  {
    name: 'dateSale',
    label: 'Disposed Date',
    options: {
      customBodyRender: CustomBodyRenderDate,
    },
    type: columnType.date,
  },
  {
    name: 'symbol',
    label: 'Symbol',
  },
  {
    name: 'cusip',
    label: 'Cusip',
  },
  {
    name: 'symbolDescription',
    label: 'Symbol Description',
  },
  {
    name: 'cost',
    label: 'Cost',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'proceed',
    label: 'Proceed',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'netAmount',
    label: 'Net Amount',
    type: columnType.amount,
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'accruedMarketDiscount',
    label: 'Accrued Market Discount',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'washSaleLossDisallowed',
    label: 'Wash Sale Loss Disallowed',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'currentYearRealized',
    label: 'Current Year Realized',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'currentYearUnrealized',
    label: 'Current Year Unrealized',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'priorYearRealized',
    label: 'Prior Year Realized',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'aggregateProfitLoss',
    label: 'Aggregate Profit Loss',
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'entryType',
    label: 'Entry Type',
  },
  {
    name: 'sellTrnsId',
    label: 'Sell Trns ID',
  },
  {
    name: 'buyTrnsId',
    label: 'Buy Trns ID',
  },
  {
    name: 'qty',
    label: 'Qty',
  },
  {
    name: 'sellPrice',
    label: 'Sell Price',
    type: columnType.price,
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'buyPrice',
    label: 'Buy Price',
    type: columnType.price,
    options: {
      customBodyRender: CustomBodyRenderCurrencyLeft,
    },
  },
  {
    name: 'shortTerm',
    label: 'Short Term',
    options: {
      setCellProps: () => ({
        align: 'center',
      }),
      customBodyRender: CustomBodyRenderBoolean,
    },
    type: columnType.bool,
  },
  {
    name: 'longTerm',
    label: 'Long Term',
    options: {
      setCellProps: () => ({
        align: 'center',
      }),
      customBodyRender: CustomBodyRenderBoolean,
    },
    type: columnType.bool,
  },
];

const options = {
  responsive: 'standard',
  selectableRows: 'none',
};
// End Table Settings

export default function Form1099BModal(props) {
  const classes = useStyles();
  const [rows, setRows] = React.useState([]);

  const { open, close } = props;
  useEffect(() => {
    async function init() {
      let value = props.data;

      const data = await form1099bDetails(value);
      setRows(data.form1099BDetailsList);
    }

    init();
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    close();
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={close}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div style={{ width: 1500 }}>
          <div className={classes.modalHeader}>
            <div>
              <Text mt={3} variant="h2" label="Form 1099 B Details" />
            </div>
            <div className={classes.closeButton}>
              <CloseButton onClick={handleClose} />
            </div>
          </div>
          <div className={classes.modalBody}>
            <div>
              <Table
                title={'Form 1099 B Details'}
                data={rows}
                columns={form1099BColumns}
                options={options}
              />
            </div>
          </div>
          <div className={classes.modalFooter}>
            <Button label="Close" onClick={handleClose} />
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
